import React, { useState } from 'react';
import { Modal, Button, Typography, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createWorkspaceFolderStructure } from '@/api/documents';
import { useAuth } from '@/contexts/AuthContext';

const { Title, Text } = Typography;

interface DocumentSetupModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export const DocumentSetupModal: React.FC<DocumentSetupModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const { t } = useTranslation('documents');
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  const createFolderStructureMutation = useMutation({
    mutationFn: async () => {
      if (!firestoreUser?.workspace?.id || !firestoreUser?.workspace?.slug) {
        throw new Error('Workspace information not available');
      }
      
      const language = (firestoreUser?.workspace as any)?.language || 'en';
      return createWorkspaceFolderStructure(
        firestoreUser.workspace.id,
        firestoreUser.workspace.slug,
        language
      );
    },
    onSuccess: () => {
      message.success(t('generate.success', 'Document structure created successfully!'));
      queryClient.invalidateQueries({ queryKey: ['workspace-folder-structure'] });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error creating folder structure:', error);
      message.error(t('generate.error', 'Failed to create document structure. Please try again.'));
    }
  });

  const handleCreateStructure = async () => {
    setIsCreating(true);
    try {
      await createFolderStructureMutation.mutateAsync();
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal
      title={t('generate.title', 'Setup Document Structure')}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="create"
          type="primary"
          loading={isCreating}
          onClick={handleCreateStructure}
        >
          {t('generate.button', 'Create Structure')}
        </Button>
      ]}
      width={500}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          {t('generate.description', 'Create Document Folders')}
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          {t('generate.details', 'This will create the initial folder structure for organizing your workspace documents including Administration, Properties, Residents, Employees, Finance, and Maintenance folders.')}
        </Text>
        
        <Space direction="vertical" size="small" style={{ textAlign: 'left', width: '100%' }}>
          <Text>📁 Administration</Text>
          <Text>🏠 Properties</Text>
          <Text>👥 Residents</Text>
          <Text>👨‍💼 Employees</Text>
          <Text>💰 Finance</Text>
          <Text>🔧 Maintenance</Text>
        </Space>
      </div>
    </Modal>
  );
};

export default DocumentSetupModal;
